import { useState } from "react";
import {
  confirmSignUp,
  signIn,
  signUp
} from "aws-amplify/auth";

export function AuthPanel({
  contextLabel = "",
  contextName = "",
  contextShortName = "",
  onAuthenticated,
  surface = "owner"
}) {
  const [mode, setMode] = useState("sign-in");
  const [confirmStep, setConfirmStep] = useState(false);
  const [form, setForm] = useState({
    email: "",
    password: "",
    preferredUsername: "",
    confirmationCode: ""
  });
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const isSignUp = mode === "sign-up";
  const isPublic = surface === "public";
  const brandName = isPublic && contextName ? contextName : "RangeGrid";
  const brandMark = isPublic && contextShortName ? contextShortName : "RG";
  const surfaceLabel = isPublic
    ? (contextLabel || "Membership signup")
    : "Owner portal";
  const normalizedContextLabel = surfaceLabel.toLowerCase();
  const publicContinuation = normalizedContextLabel.includes("signup") || normalizedContextLabel.includes("checkout")
    ? "membership signup"
    : "member access";

  function updateField(name, value) {
    setForm((current) => ({ ...current, [name]: value }));
  }

  function switchMode(nextMode) {
    setMode(nextMode);
    setConfirmStep(false);
    setMessage("");
    setError("");
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    setError("");

    try {
      if (isSignUp) {
        if (confirmStep) {
          await confirmSignUp({
            username: form.email,
            confirmationCode: form.confirmationCode
          });
          setMessage("Account confirmed. Sign in with the same ArmorySync identity.");
          setMode("sign-in");
          setConfirmStep(false);
          return;
        }

        const result = await signUp({
          username: form.email,
          password: form.password,
          options: {
            userAttributes: {
              email: form.email,
              preferred_username: form.preferredUsername || form.email
            }
          }
        });

        if (result?.nextStep?.signUpStep === "CONFIRM_SIGN_UP") {
          setConfirmStep(true);
          setMessage("Verification code sent to your email.");
        } else {
          setMessage("Account created. Sign in to continue.");
          setMode("sign-in");
        }
        return;
      }

      await signIn({ username: form.email, password: form.password });
      await onAuthenticated();
    } catch (err) {
      setError(err?.message || "Authentication failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="auth-shell">
      <section className="auth-panel">
        <div className="brand-block auth-brand">
          <div className="brand-mark">{brandMark}</div>
          <div>
            <strong>{brandName}</strong>
            <span>{surfaceLabel}</span>
          </div>
        </div>

        <div className="panel-header">
          <p className="eyebrow">ArmorySync identity</p>
          <h1>{isSignUp ? "Create your account" : `Sign in${isPublic && contextName ? ` for ${contextName}` : " to RangeGrid"}`}</h1>
          <p>
            {isPublic
              ? `Use your ArmorySync account. ${contextName ? `${contextName} ${publicContinuation}` : `Your ${publicContinuation}`} will continue after sign-in.`
              : "Use the same account you use for Range Pocket. RangeGrid adds range owner access on top of your ArmorySync identity."}
          </p>
        </div>

        <div className="auth-tabs" role="tablist" aria-label="Authentication mode">
          <button
            type="button"
            className={!isSignUp ? "is-active" : ""}
            onClick={() => switchMode("sign-in")}
          >
            Sign in
          </button>
          <button
            type="button"
            className={isSignUp ? "is-active" : ""}
            onClick={() => switchMode("sign-up")}
          >
            Sign up
          </button>
        </div>

        <form className="auth-form" onSubmit={handleSubmit}>
          {isSignUp && confirmStep ? (
            <>
              <label>
                Email
                <input
                  type="email"
                  value={form.email}
                  onChange={(event) => updateField("email", event.target.value)}
                  autoComplete="email"
                  required
                />
              </label>
              <label>
                Verification code
                <input
                  value={form.confirmationCode}
                  onChange={(event) => updateField("confirmationCode", event.target.value)}
                  autoComplete="one-time-code"
                  required
                />
              </label>
            </>
          ) : (
            <>
              <label>
                Email
                <input
                  type="email"
                  value={form.email}
                  onChange={(event) => updateField("email", event.target.value)}
                  autoComplete="email"
                  required
                />
              </label>

              {isSignUp && (
                <label>
                  Username
                  <input
                    value={form.preferredUsername}
                    onChange={(event) => updateField("preferredUsername", event.target.value)}
                    autoComplete="username"
                    required
                  />
                </label>
              )}

              <label>
                Password
                <input
                  type="password"
                  value={form.password}
                  onChange={(event) => updateField("password", event.target.value)}
                  autoComplete={isSignUp ? "new-password" : "current-password"}
                  required
                />
              </label>
            </>
          )}

          <button type="submit" disabled={busy}>
            {busy ? "Please wait..." : isSignUp && confirmStep ? "Confirm account" : isSignUp ? "Create account" : "Sign in"}
          </button>
        </form>

        {message && <div className="notice is-success">{message}</div>}
        {error && <div className="notice is-error">{error}</div>}
      </section>
    </main>
  );
}
